import { useCallback, useState } from 'react';
import { Button, Field } from '@ontruck/orc';
import { DistanceCell, FormatPrice } from 'utils/format';
import Tippy from '@tippyjs/react';

import { getDiffValues, valueExists, PositiveSign } from './utils';

const t = {
  create_route: 'Create route',
  cost: 'Cost',
  price: 'Price',
  margin: 'Margin',
  distance: 'Distance',
  create_route_header: 'Create a route',
  upgrade_route_header: 'Upgrade route',
};

const ENTER = 13;

const EditCost = ({ defaultValue, onSave }) => {
  const [cost, setCost] = useState(defaultValue);

  const handleChange = useCallback((e, { value }) => {
    setCost(value);
  }, []);

  const onSaveCost = useCallback(() => {
    onSave(cost);
  }, [onSave, cost]);

  const handleKeyUp = useCallback(
    (e) => {
      if (e.keyCode !== ENTER) {
        return;
      }
      onSaveCost();
    },
    [onSaveCost]
  );

  return (
    <div style={{ display: 'flex' }}>
      <div style={{ marginRight: 8 }}>
        <Field
          label="Fixed cost"
          name="routing_fixed_cost"
          id="routing_fixed_cost"
          value={cost}
          asideRightText="€"
          width="XS"
          onChange={handleChange}
          onKeyUp={handleKeyUp}
        />
      </div>
      <div>
        <Button text="Save" skin="primary" onClick={onSaveCost} />
      </div>
    </div>
  );
};

const RoutingRoutePlan = ({ routePlan, onChangeCost, onOpenMapClick, journeyForUpgrade }) => {
  const [visible, setVisible] = useState(false);
  const show = () => setVisible(true);
  const hide = () => setVisible(false);

  const [modifiedCost, setModifiedCost] = useState('');

  const handleSaveCost = useCallback(
    (cost) => {
      setModifiedCost(cost);
      onChangeCost(cost);
      hide();
    },
    [onChangeCost]
  );

  if (!routePlan) {
    return null;
  }

  const net_margin = routePlan?.net_full_price - routePlan?.net_full_cost;

  const { diffDistance, diffDistanceMiles, diffNetFullCost, diffNetFullPrice, diffNetMargin } =
    getDiffValues({ ...routePlan, net_margin }, journeyForUpgrade) || {};

  return (
    <>
      <div className="distance push">
        <div className="hint">{t.distance}</div>
        <div className="amount" data-testid="distanceValue">
          <DistanceCell
            distance={routePlan?.distance}
            distanceMiles={routePlan?.distance_miles}
            country={routePlan?.country || 'ES'}
          />
          <div className="distance-link">
            <Button text="View map" nude onClick={onOpenMapClick} />
          </div>
        </div>
        {(!!valueExists(diffDistance) || !!valueExists(diffDistanceMiles)) && (
          <div className="faded-out" data-testid="diff-distance">
            <PositiveSign value={diffDistance} />
            <DistanceCell
              distance={diffDistance}
              distanceMiles={diffDistanceMiles}
              country={routePlan?.country || 'ES'}
            />
          </div>
        )}
      </div>
      <div className="amount-container">
        <div className="hint">{t.cost}</div>
        <div className="amount faded-in" data-testid="costValue">
          {
            <FormatPrice
              currency={routePlan.currency}
              value={modifiedCost || routePlan.net_full_cost}
            />
          }
          <Tippy
            maxWidth="none"
            interactive
            content={
              <EditCost
                defaultValue={modifiedCost || routePlan.net_full_cost}
                onSave={handleSaveCost}
              />
            }
            placement="top"
            visible={visible}
            onClickOutside={hide}
            theme="light"
          >
            <div className="edit-link" tabIndex="0">
              <Button text="Edit" nude onClick={visible ? hide : show} />
            </div>
          </Tippy>
        </div>
        {!!valueExists(diffNetFullCost) && (
          <div className="faded-out" data-testid="diff-cost">
            <PositiveSign value={diffNetFullCost} />
            <FormatPrice currency={routePlan.currency} value={diffNetFullCost} />
          </div>
        )}
      </div>
      <div className="amount-container">
        <div className="hint">{t.price}</div>
        <div className="amount faded-out" data-testid="priceValue">
          {<FormatPrice currency={routePlan.currency} value={routePlan.net_full_price} />}
        </div>
        {!!valueExists(diffNetFullPrice) && (
          <div className="faded-out" data-testid="diff-price">
            <PositiveSign value={diffNetFullPrice} />
            <FormatPrice currency={routePlan.currency} value={diffNetFullPrice} />
          </div>
        )}
      </div>
      <div>
        <div className="hint">{t.margin}</div>
        <div className="amount faded-in" data-testid="marginValue">
          {<FormatPrice currency={routePlan.currency} value={net_margin} />}
        </div>
        {!!valueExists(diffNetMargin) && (
          <div className="faded-out" data-testid="diff-margin">
            <PositiveSign value={diffNetMargin} />
            <FormatPrice currency={routePlan.currency} value={diffNetMargin} />
          </div>
        )}
      </div>
    </>
  );
};

export default RoutingRoutePlan;
